/**
 * Master Coach Agent — LLM-powered router.
 *
 * Phase B: returns a `RouterIntent` produced by an OpenAI call with
 * `response_format: { type: 'json_object' }`, validated by Zod against
 * `RouterIntentSchema`. Returns `null` on parse failure or when
 * confidence falls below `ROUTER_CONFIDENCE_THRESHOLD`, signaling the
 * dispatcher to use the regex fallback.
 *
 * On by default: this is the primary dispatcher, with regex intent
 * detection in `CoachSteve.tsx` used only as its fallback. Force it off
 * (regex-only) via `VITE_COACH_AGENT_ROUTER=off` or
 * `localStorage.coachAgentRouter = 'off'` for debugging.
 */

import { generateAICompletion, generateAIContent } from '../../../api/ai';
import type { OpenAITool } from '../../../api/ai';
import { listAgents } from './registry';
import {
  ROUTER_CONFIDENCE_THRESHOLD,
  RouterIntentSchema,
  type ValidatedRouterIntent,
} from './routerSchema';
import { logRouted } from './routerLogger';
import type { AgentRunContext, ChatMessage, RouterIntent } from './types';

/**
 * Check whether the LLM router should be used for the current session.
 * Safe to call on the server (no `window`) and in tests.
 */
export function isRouterEnabled(): boolean {
  // Highest priority: developer/QA toggle in localStorage.
  try {
    if (typeof window !== 'undefined' && window.localStorage) {
      const flag = window.localStorage.getItem('coachAgentRouter');
      if (flag === 'on') return true;
      if (flag === 'off') return false;
    }
  } catch {
    // localStorage may throw in private mode — fall through.
  }
  // Build-time env flag.
  const env = import.meta.env?.VITE_COACH_AGENT_ROUTER;
  if (typeof env === 'string') {
    return env.toLowerCase() === 'on' || env === '1' || env.toLowerCase() === 'true';
  }
  // Default on: the LLM router is the primary dispatcher, with regex intent
  // detection acting only as its fallback (on parse failure, low confidence,
  // or an LLM call error). Set localStorage.coachAgentRouter = 'off' (or
  // VITE_COACH_AGENT_ROUTER=off) to force regex-only for debugging.
  return true;
}

/**
 * When true, route via native OpenAI function calling instead of
 * `response_format: { type: 'json_object' }`. Same gating semantics as
 * `isRouterEnabled` — opt-in via `localStorage.coachAgentRouterTools`
 * or `VITE_COACH_AGENT_ROUTER_TOOLS`.
 */
export function isToolCallingRouterEnabled(): boolean {
  try {
    if (typeof window !== 'undefined' && window.localStorage) {
      const flag = window.localStorage.getItem('coachAgentRouterTools');
      if (flag === 'on') return true;
      if (flag === 'off') return false;
    }
  } catch {
    // ignore
  }
  const env = import.meta.env?.VITE_COACH_AGENT_ROUTER_TOOLS;
  if (typeof env === 'string') {
    return env.toLowerCase() === 'on' || env === '1' || env.toLowerCase() === 'true';
  }
  return false;
}

interface BuildPromptArgs {
  team: AgentRunContext['team'] | null;
  pendingFlow: string | null;
  recentMessages: ChatMessage[];
}

/**
 * Build a description block listing every registered agent's name,
 * description, and applicable sports. Kept short to control token cost.
 */
function buildAgentCatalog(): string {
  return listAgents()
    .filter((a) => a.name !== 'help')
    .map((a) => {
      const sports =
        a.applicableSports === 'all'
          ? 'all sports'
          : a.applicableSports.join(', ');
      return `- ${a.name}: ${a.description} (sports: ${sports})`;
    })
    .join('\n');
}

/** Build the system prompt the master agent uses for JSON routing. */
export function buildRouterPrompt({ team, pendingFlow }: BuildPromptArgs): string {
  const teamLine = team
    ? `Current team: ${team.name} (${team.sport}${team.ageLevel ? `, ${team.ageLevel}` : ''}).`
    : 'No team selected yet.';
  const pendingLine = pendingFlow
    ? `Pending flow: ${pendingFlow} — set "interrupt": true if the user clearly switches to a different domain.`
    : 'No pending flow.';

  return `You are the routing brain for Coach Steve, a youth-sports coaching assistant. Pick the single best sub-agent to handle the latest user message.

${teamLine}
${pendingLine}

Available sub-agents:
${buildAgentCatalog()}
- general_chat: fallback when no specialized sub-agent fits (general coaching/strategy questions, drills explanation, etc.).

Respond with a STRICT JSON object — no prose, no Markdown — using this schema:
{
  "sub_agent": "<one of: practice | gamePlan | game | formation | depthChart | roster | stats | attendance | playbook | general_chat>",
  "action": "<short verb describing the operation, e.g. 'createPractice', 'addDrillToPractice', 'addPlayer', 'setInjuryStatus', 'assignToFormation', 'createGame', 'queryStats', 'general_chat'>",
  "params": { "<agent-specific params>": "<values>" },
  "confidence": <number between 0 and 1>,
  "clarification": "<optional short question if confidence is low>",
  "interrupt": <true/false — only true if the user is switching domains while a flow is pending>,
  "follow_up": "<optional: verbatim secondary intent text when the message contains two genuinely distinct operations>",
  "follow_up_agent": "<optional: sub-agent name that handles follow_up — set this whenever you set follow_up so the system can dispatch directly without re-routing>"
}

Routing rules:
- "add a drill to practice" / "put a drill on tomorrow's practice" -> practice / addDrillToPractice (not a generic new full plan request).
- "create a practice" / "make a practice plan" -> practice / createPractice.
- "create a game plan" (Football only) -> gamePlan / createGamePlan.
- "create a game" / "add a game" / "schedule a game" -> game / createGame.
- "create a formation" / "save formation" -> formation / createFormation (informational only).
- "put X at QB in Spread" / "assign #5 to RB" / "move Ace to LDT in 4-3" -> depthChart / assignToFormation. Football and Flag Football only.
- "add John Doe #5", "add a player", "new player" -> roster / addPlayer, params: { firstName, lastName, number (integer, omit if none), isSub (boolean) }. Extract ALL available player details from the message into params so downstream agents don't need to re-parse the text. If the message names MULTIPLE players to add (e.g. "add Isiah Thomas, Kobe Bryant, and Patrick Ewing as guards"), instead return params: { players: [{ firstName, lastName, number, isSub }, ...] } listing EVERY player mentioned — never drop any of them, even when they share one position group stated once for the whole list.
- "put [player] in the Nth spot", "move [player] to spot N", "move [player] up/down to N", "Nth spot of the lineup/batting order" -> depthChart / updateBattingOrder. Baseball/Softball only. This is a batting order slot change, NOT a roster add — even when a player name is present.
- "mark Cole out", "set #5 as questionable", "Finn is sick", "activate Mike" -> roster / setInjuryStatus. Never route healthy/sick statuses to depthChart.
- Any question about player stats, performance, or game stats (Baseball/Softball) -> stats / queryStats. No params needed — the stats agent fetches all the data and the LLM interprets it. Route here even when the question uses past tense, names a specific player, or asks about a specific game. Examples: "who has the most HR", "what were Aiden's stats last night", "how did Nate do against Kaneland", "last 5 games for Cole", "who has hit a double this season", "what's Cole's ERA", "who's been hot lately", "how many RBI does Aiden have", "which players had hits in our last game".
- Any question about practice/game attendance, absences, no-shows, tardiness, or attendance-related eligibility -> attendance / queryAttendance. No params needed — the attendance agent fetches all the records and the LLM interprets them. This is COMPLETELY SEPARATE from player health/injury status (H/Q/D/O) — "missed practice" and "absence" mean an attendance record, NOT an injury. Examples: "who has missed the most practices", "any absences in the last 2 weeks", "who showed up late", "summarize the attendance records", "is anyone at risk of losing eligibility for missing too many practices", "how many kids have missed practice".
- Otherwise -> general_chat / general_chat with confidence 0.8+.
- If the message is ambiguous, pick general_chat with low confidence and provide a clarification question.
- Compound messages: if the user's message contains two clearly distinct operations (e.g. "add Jackson to the roster AND put him in the 10th batting spot"), route the PRIMARY action in the main fields and store the SECONDARY intent verbatim in "follow_up". The system will automatically process "follow_up" after the primary action completes. Only set "follow_up" when there are two genuinely separate actions.`;
}

/**
 * Build the OpenAI tool array used by the function-calling router.
 * Single tool, `route_to_agent`, mirrors the JSON envelope schema so the
 * downstream Zod validation is identical for both code paths.
 */
function buildRouterTool(): OpenAITool {
  return {
    type: 'function',
    function: {
      name: 'route_to_agent',
      description:
        "Route the user's message to the right Coach Steve sub-agent and surface the parameters that agent needs.",
      parameters: {
        type: 'object',
        additionalProperties: false,
        required: ['sub_agent', 'action', 'confidence'],
        properties: {
          sub_agent: {
            type: 'string',
            enum: [
              'practice',
              'gamePlan',
              'game',
              'formation',
              'depthChart',
              'roster',
              'stats',
              'attendance',
              'playbook',
              'general_chat',
            ],
            description: 'Name of the sub-agent that should handle this message.',
          },
          action: {
            type: 'string',
            description:
              "Verb describing the operation (e.g. 'createPractice', 'addPlayer', 'setInjuryStatus').",
          },
          params: {
            type: 'object',
            additionalProperties: true,
            description: 'Agent-specific arguments parsed from the message.',
          },
          confidence: {
            type: 'number',
            minimum: 0,
            maximum: 1,
            description: 'How confident the router is in this routing decision.',
          },
          clarification: {
            type: 'string',
            description: 'Optional follow-up question if confidence is low.',
          },
          interrupt: {
            type: 'boolean',
            description:
              'True if the user clearly switched domains while a flow is pending.',
          },
          follow_up: {
            type: 'string',
            description:
              'Verbatim secondary intent text when the message has two distinct operations. The system processes this automatically after the primary action completes.',
          },
          follow_up_agent: {
            type: 'string',
            enum: [
              'practice', 'gamePlan', 'game', 'formation', 'depthChart',
              'roster', 'stats', 'attendance', 'playbook', 'general_chat',
            ],
            description: 'Sub-agent that handles follow_up. Always set this when you set follow_up so the system dispatches directly without re-routing.',
          },
        },
      },
    },
  };
}

/**
 * Phase B router. Returns a validated `RouterIntent` or `null` to fall
 * back to the regex dispatcher. Uses OpenAI function calling when
 * `coachAgentRouterTools` is enabled (Phase E), otherwise JSON mode.
 */
export async function routeUserMessage(
  text: string,
  ctx: AgentRunContext,
  pendingFlow?: string | null
): Promise<RouterIntent | null> {
  if (!isRouterEnabled()) return null;
  if (!text.trim()) return null;

  const systemPrompt = buildRouterPrompt({
    team: ctx.team,
    pendingFlow: pendingFlow ?? null,
    recentMessages: ctx.recentMessages,
  });
  const recentTurns = ctx.recentMessages.slice(-6).map((m) => ({
    role: m.role as 'user' | 'assistant',
    content: m.content,
  }));
  const messages = [
    { role: 'system' as const, content: systemPrompt },
    ...recentTurns,
    { role: 'user' as const, content: text },
  ];

  let parsed: ValidatedRouterIntent;
  try {
    if (isToolCallingRouterEnabled()) {
      const completion = await generateAICompletion(messages, {
        tools: [buildRouterTool()],
        tool_choice: { type: 'function', function: { name: 'route_to_agent' } },
        timeout: 12000,
      });
      const call = completion.toolCalls[0];
      if (!call) {
        logRouted({ text, source: 'router', intent: null, routedTo: 'no-tool-call' });
        return null;
      }
      parsed = RouterIntentSchema.parse(JSON.parse(call.function.arguments));
    } else {
      const raw = await generateAIContent(messages, {
        response_format: { type: 'json_object' },
        timeout: 12000,
      });
      parsed = RouterIntentSchema.parse(JSON.parse(raw));
    }
  } catch (err) {
    logRouted({ text, source: 'router', intent: null, routedTo: `error:${(err as Error).message}` });
    return null;
  }

  if (parsed.confidence < ROUTER_CONFIDENCE_THRESHOLD) {
    logRouted({ text, source: 'router', intent: parsed, routedTo: 'low-confidence' });
    return null;
  }

  logRouted({ text, source: 'router', intent: parsed, routedTo: parsed.sub_agent });
  return parsed;
}
